<template>
    <app-layout>
        <div class="grid grid-cols-12 gap-4">
            <div class="col-span-3">
                <div class="bg-white p-3 shadow-lg rounded-sm border border-gray-200">
                    <div class="flex items-center justify-between mb-3">
                        <div class="sm:text-md xl:text-lg text-sky font-bold">
                            Expédition: N°{{ $page.props.travel.reference }}
                        </div>
                        <i @click.prevent="printAll" class="fad fa-print text-greendack-500 cursor-pointer" title="Imprimer le récapitulatif"></i>
                    </div>
                    <ul class="mb-4">
                        <li :class="'text-lg font-bold text-' + $page.props.travel.status_color + '-500 uppercase text-center py-1 mb-1 border border-' + $page.props.travel.status_color + '-500 bg-' + $page.props.travel.status_color + '-100'">
                            Statut Expédition: {{ $page.props.travel.status_text }}
                        </li>
                        <li class="sm:text-xs xl:text-sm w-full flex justify-between py-2 border-b border-gray-200">
                            <div class="flex items-center text-gray-400">
                                <i class="fad fa-calendar mr-1"></i>
                                <div class="font-semibold">Date de départ:</div>
                            </div>
                            <div class="font-bold text-sky capitalize">{{ formattedDate($page.props.travel.departure_date) }}</div>
                        </li>
                        <li class="sm:text-xs xl:text-sm w-full flex justify-between py-2 border-b border-gray-200">
                            <div class="flex items-center text-gray-400">
                                <i class="fad fa-calendar mr-1"></i>
                                <div class="font-semibold">Date d'arrivé:</div>
                            </div>
                            <div class="font-bold text-sky capitalize">{{ formattedDate($page.props.travel.arrival_date) }}</div>
                        </li>
                        <li class="sm:text-xs xl:text-sm w-full flex justify-between py-2 border-b border-gray-200">
                            <div class="flex items-center text-gray-400">
                                <i class="fad fa-calendar mr-1"></i>
                                <div class="font-semibold">Début de collecte:</div>
                            </div>
                            <div class="font-bold text-sky capitalize">{{ formattedDate($page.props.travel.collection_start_at) }}</div>
                        </li>
                        <li class="sm:text-xs xl:text-sm w-full flex justify-between py-2 border-b border-gray-200">
                            <div class="flex items-center text-gray-400">
                                <i class="fad fa-calendar mr-1"></i>
                                <div class="font-semibold">Fin de collecte:</div>
                            </div>
                            <div class="font-bold text-sky capitalize">{{ formattedDate($page.props.travel.collection_end_at) }}</div>
                        </li>
                        <li class="sm:text-xs xl:text-sm w-full flex justify-between py-2 border-b border-gray-200">
                            <div class="flex items-center text-gray-400">
                                <i class="fad fa-flag mr-1"></i>
                                <div class="font-semibold">Pays de départ:</div>
                            </div>
                            <div class="font-bold text-sky">{{ $page.props.travel.departure_country.name }}</div>
                        </li>
                        <li class="sm:text-xs xl:text-sm w-full flex justify-between py-2 border-b border-gray-200">
                            <div class="flex items-center text-gray-400">
                                <i class="fad fa-flag mr-1"></i>
                                <div class="font-semibold">Pays d'arrivée:</div>
                            </div>
                            <div class="font-bold text-sky">{{ $page.props.travel.arrival_country.name }}</div>
                        </li>
                        <li v-if="($page.props.travel.container_company || $page.props.travel.container_size)" class="sm:text-xs xl:text-sm w-full flex justify-between py-2 border-b border-gray-200">
                            <div class="flex items-center text-gray-400">
                                <i class="fad fa-truck-container mr-1 "></i> :
                                <span class="ml-1" :class="{ 'text-sky font-semibold': $page.props.travel.container_company }">
                                    {{ $page.props.travel.container_company ? $page.props.travel.container_company : 'N/A' }}
                                </span>
                            </div>
                            <div class="flex items-center text-gray-400">
                                <i class="fad fa-container-storage mr-1"></i> :
                                <span class="ml-1" :class="{ 'text-sky font-semibold': $page.props.travel.container_size }">
                                    {{ $page.props.travel.container_size ? $page.props.travel.container_size+' Pieds' : 'N/A' }}
                                </span>
                            </div>
                        </li>
                        <li v-if="$page.props.travel.warehouse" class="sm:text-xs xl:text-sm w-full flex justify-between py-2 border-b border-gray-200">
                            <div class="flex items-center text-gray-400">
                                <svg class="h-4 w-4 fill-current shrink-0" viewBox="0 0 20 20">
                                    <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                                </svg>
                                <div class="font-semibold">Entrépôt:</div>
                            </div>
                            <div class="font-bold text-sky">{{ $page.props.travel.warehouse.country.name + ' - ' + $page.props.travel.warehouse.label }}</div>
                        </li>
                    </ul>
                    <div v-if="$page.props.travel.clauses" class="mb-4">
                        <div class="flex items-center text-gray-400 sm:text-xs xl:text-sm mb-2">
                            <i class="fad fa-file-contract mr-1"></i>
                            <div class="font-semibold">Clauses d'expédition</div>
                        </div>

                        <div class="shipping-clauses text-xs text-gray-700 bg-gray-50 border border-gray-200
                            rounded px-1.5 max-h-64 overflow-y-auto leading-relaxed"
                            v-html="renderClauses($page.props.travel.clauses)"
                        />
                    </div>
                    <div class="mb-2" v-if="$page.props.permissions.canAddExpense">
                        <div class="flex items-center justify-between">
                            <label class="block text-sm font-bold mb-1" for="amount">Montant total dépensé en {{ currency ? currency.symbol : '' }}:</label>
                        </div>
                        <input id="amount" v-model="spentForm.amount" class="form-input w-full mb-2 text-right"
                            type="number" placeholder="0" min="0"
                        >
                        <button class="btn w-full bg-greendack-500 hover:bg-greendack-400 text-white uppercase"
                            :disabled="spentForm.processing" @click.prevent="updateTotalSpent"
                            :class="{ 'opacity-25': spentForm.processing }"
                        >
                            Appliquer vos dépenses
                            <svg v-if="spentForm.processing" class="animate-spin w-5 h-5 fill-current shrink-0 ml-2" viewBox="0 0 16 16">
                                <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"></path>
                            </svg>
                            <i v-else class="fad fa-user-edit ml-2 text-lg"></i>
                        </button>
                    </div>
                    <div class="space-y-2">
                        <button v-if="$page.props.permissions.canEmbeddedAllPackage"
                            class="btn w-full bg-yellow-500 hover:bg-yellow-400 text-white uppercase"
                            :disabled="statusPackForm.processing" @click.prevent="embeddedAllPackage"
                            :class="{ 'opacity-25': statusPackForm.processing }"
                        >
                            Embarquer tous les colis
                            <svg v-if="statusPackForm.processing" class="animate-spin w-4 h-4 fill-current shrink-0 ml-2" viewBox="0 0 16 16">
                                <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"></path>
                            </svg>
                            <i v-else class="fad fa-solid fa-anchor ml-2 text-lg"></i>
                        </button>
                        <div v-if="$page.props.permissions.canNotStartTravel" class="border-l-4 bg-yellow-100 border-yellow-500 rounded-b text-yellow-500 p-1 shadow-md">
                            <div class="flex items-center">
                                <i class="fad fa-solid fa-anchor text-3xl text-yellow-500 mr-3"></i>
                                <div>
                                    <p class="font-bold text-yellow-500 text-xs">Information</p>
                                    <div class="block text-sm">
                                        <label class="flex items-center leading-tight">
                                            <span class="text-small font-semibold">
                                                Tous les colis doivent être déclarés comme étant embarqués avant de pouvoir démarrer l'expédition.
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button v-if="$page.props.permissions.canStartTravel"
                            class="btn w-full bg-greendack-500 hover:bg-greendack-400 text-white uppercase"
                            :disabled="statusForm.processing" @click.prevent="startTravel"
                            :class="{ 'opacity-25': statusForm.processing }"
                        >
                            Démarrer l'expédition
                            <svg v-if="statusForm.processing" class="animate-spin w-4 h-4 fill-current shrink-0 ml-2" viewBox="0 0 16 16">
                                <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"></path>
                            </svg>
                            <i v-else class="fad fa-solid fa-hourglass-start ml-2 text-lg"></i>
                        </button>
                        <div v-if="$page.props.permissions.canStartTravel" class="border-l-4 bg-greendack-100 border-greendack-700 rounded-b text-greendack-700 p-1 shadow-md">
                            <div class="flex items-center">
                                <i class="fad fa-solid fa-hourglass-start text-3xl text-greendack-700 mr-3"></i>
                                <div>
                                    <p class="font-bold text-greendack-700 text-xs">Attention</p>
                                    <div class="block text-sm">
                                        <label class="flex items-center leading-tight">
                                            <span class="text-small font-semibold">
                                                Après le démarrage de l'expédition, il ne sera plus possible d'ajouter, de modifier ou de supprimer un colis.
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button v-if="$page.props.permissions.canFinishTravel"
                            class="btn w-full bg-orange-500 hover:bg-orange-400 text-white uppercase"
                            :disabled="statusFinishedForm.processing" @click.prevent="finishTravel"
                            :class="{ 'opacity-25': statusFinishedForm.processing }"
                        >
                            Terminer l'expédition
                            <svg v-if="statusFinishedForm.processing" class="animate-spin w-4 h-4 fill-current shrink-0 ml-2" viewBox="0 0 16 16">
                                <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"></path>
                            </svg>
                            <i v-else class="fad fa-siren-on ml-2 text-lg"></i>
                        </button>
                        <button v-if="$page.props.permissions.canMakeTravelHasAvailable"
                            class="btn w-full bg-marron-500 hover:bg-marron-400 text-white uppercase"
                            :disabled="statusForm.processing" @click.prevent="makeTravelHasAvailable"
                            :class="{ 'opacity-25': statusForm.processing }"
                        >
                            Expédition disponible
                            <svg v-if="statusForm.processing" class="animate-spin w-4 h-4 fill-current shrink-0 ml-2" viewBox="0 0 16 16">
                                <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"></path>
                            </svg>
                            <i v-else class="fad fa-solid fa-hand-holding-box ml-2 text-lg"></i>
                        </button>
                        <button v-if="$page.props.permissions.canMakeTravelHasClosed"
                            class="btn w-full bg-red-500 hover:bg-red-400 text-white uppercase"
                            :disabled="statusForm.processing" @click.prevent="makeTravelHasClosed"
                            :class="{ 'opacity-25': statusForm.processing }"
                        >
                            Fermer l'expédition
                            <svg v-if="statusForm.processing" class="animate-spin w-4 h-4 fill-current shrink-0 ml-2" viewBox="0 0 16 16">
                                <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"></path>
                            </svg>
                            <i v-else class="fad fa-times-circle ml-2 text-lg"></i>
                        </button>
                        <div v-if="$page.props.permissions.canNotClosed" class="border-l-4 bg-red-100 border-red-500 rounded-b text-red-500 p-1 shadow-md mt-2">
                            <div class="flex items-center">
                                <i class="fad fa-engine-warning text-3xl text-red-500 mr-3"></i>
                                <div>
                                    <p class="font-bold text-red-500 text-xs">Information</p>
                                    <div class="block text-sm">
                                        <label class="flex items-center leading-tight">
                                            <span class="text-small font-semibold">
                                                L'expédition ne peut pas être fermée tant qu'il existe encore des colis en statut "<b>Disponible</b>".
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button v-if="$page.props.permissions.canSendAlertOnTravel" 
                            class="btn w-full bg-yellow hover:bg-yellow-400 text-white uppercase"
                            :disabled="delayForm.processing" @click.prevent="sendAlert"
                            :class="{ 'opacity-25': delayForm.processing }"
                        >
                            Envoyer une alerte
                            <svg v-if="delayForm.processing" class="animate-spin w-4 h-4 fill-current shrink-0 ml-2" viewBox="0 0 16 16">
                                <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"></path>
                            </svg>
                            <i v-else class="fad fa-exclamation-triangle ml-2 text-lg"></i>
                        </button>
                    </div>
                </div>
            </div>
            <div class="col-span-9">
                <div class="mb-1 pb-1">
                    <ul class="text-sm font-medium flex flex-nowrap overflow-x-scroll no-scrollbar space-x-2">
                        <li>
                            <Link :href="route('travels.show', $page.props.travel)"
                                class="whitespace-nowrap flex items-center p-2 mr-6 last:mr-0 first:pl-2 sm:first:pl-4 lg:first:pl-6 last:pr-2 sm:last:pr-4 lg:last:pr-6 rounded-md"
                                :class="route().current('travels.show')
                                    ? 'bg-sky hover:bg-sky-400 text-white font-bold'
                                    : 'bg-gray-100 text-gray-500 hover:bg-sky-400 hover:text-white border'"
                            >
                                <ViewListIcon class="w-5 h-5 shrink-0 fill-current mr-2" />
                                <span>Détails</span>
                            </Link>
                        </li>
                        <li>
                            <Link :href="route('travels.packages.index', $page.props.travel)"
                                class="whitespace-nowrap flex items-center p-2 mr-6 last:mr-0 first:pl-2 sm:first:pl-4 lg:first:pl-6 last:pr-2 sm:last:pr-4 lg:last:pr-6 rounded-md"
                                :class="route().current('travels.packages.index')
                                    ? 'bg-sky hover:bg-sky-400 text-white font-bold'
                                    : 'bg-gray-100 text-gray-500 hover:bg-sky-400 hover:text-white border'"
                            >
                                <ArchiveIcon class="w-5 h-5 shrink-0 fill-current mr-2" />
                                <span>Colis</span>
                            </Link>
                        </li>
                        <li v-if="$page.props.acl.isAdmin || $page.props.acl.isCommercial">
                            <Link :href="route('travels.requests', $page.props.travel)"
                                class="whitespace-nowrap flex items-center p-2 mr-6 last:mr-0 first:pl-2 sm:first:pl-4 lg:first:pl-6 last:pr-2 sm:last:pr-4 lg:last:pr-6 rounded-md"
                                :class="route().current('travels.requests')
                                    ? 'bg-sky hover:bg-sky-400 text-white font-bold'
                                    : 'bg-gray-100 text-gray-500 hover:bg-sky-400 hover:text-white border'"
                            >
                                <ClipboardListIcon class="w-5 h-5 shrink-0 fill-current mr-2" />
                                <span>Demandes</span>
                            </Link>
                        </li>
                        <li>
                            <Link :href="route('travels.items.index', $page.props.travel)"
                                class="whitespace-nowrap flex items-center p-2 mr-6 last:mr-0 first:pl-2 sm:first:pl-4 lg:first:pl-6 last:pr-2 sm:last:pr-4 lg:last:pr-6 rounded-md"
                                :class="route().current('travels.items.index')
                                    ? 'bg-primaryOrange hover:bg-primaryOrange-300 text-white font-bold'
                                    : 'bg-primaryOrange-50 text-gray-500 hover:bg-primaryOrange hover:text-white border border-primaryOrange-200'"
                            >
                                <ShoppingBagIcon class="w-5 h-5 shrink-0 fill-current mr-2" />
                                <span>Boutique</span>
                            </Link>
                        </li>
                        <li>
                            <Link :href="route('travels.historical', $page.props.travel)"
                                class="whitespace-nowrap flex items-center p-2 mr-6 last:mr-0 first:pl-2 sm:first:pl-4 lg:first:pl-6 last:pr-2 sm:last:pr-4 lg:last:pr-6 rounded-md"
                                :class="route().current('travels.historical')
                                    ? 'bg-sky hover:bg-sky-400 text-white font-bold'
                                    : 'bg-gray-100 text-gray-500 hover:bg-sky-400 hover:text-white border'"
                            >
                                <ClockIcon class="w-5 h-5 shrink-0 fill-current mr-2" />
                                <span>Historique</span>
                            </Link>
                        </li>
                    </ul>
                </div>

                <slot />
            </div>
        </div>
    </app-layout>
</template>

<script>
    import { marked } from 'marked';
    import { Link } from '@inertiajs/inertia-vue3';
    import AppLayout from '@/Layouts/AppLayout.vue'
    import PrintMixins from '@/Mixins/PrintMixins.vue';
    import DateFormatMixins from '@/Mixins/DateFormatMixins'
    import { ArchiveIcon, ClipboardListIcon, ViewListIcon, ClockIcon, ShoppingBagIcon } from '@heroicons/vue/solid'

    export default ({
        props: {
            currency: {
                type: Object,
                default: null
            }
        },

        components: {
            Link,
            marked,
            AppLayout,
            ClockIcon,
            ArchiveIcon,
            ViewListIcon,
            ShoppingBagIcon,
            ClipboardListIcon
        },

        data() {
            return {
                spentForm: this.$inertia.form({
                    amount: null,
                }),
                statusForm: this.$inertia.form({
                }),
                statusPackForm: this.$inertia.form({
                }),
                statusFinishedForm: this.$inertia.form({
                }),
                delayForm: this.$inertia.form({
                    message: null,
                }),
            }
        },

        mixins: [PrintMixins, DateFormatMixins],

        mounted() {
            this.spentForm.amount = this.$page.props.travel.total_spent
        },

        methods: {
            renderClauses(clauses) {
                return marked.parse(clauses || '')
            },

            updateTotalSpent() {
                this.spentForm.put(route('travels.update-total-spent', this.$page.props.travel), {
                    preserveScroll: true,
                    onSuccess: () => {
                        this.$swal.fire(
                            'Dépenses',
                            'Total des dépenses mis à jour avec succès.',
                            'success'
                        )
                    },
                })
            },

            printAll() {
                const url = route('travels.print', {travel: this.$page.props.travel})

                if (this.isMobile || this.isTablet) {
                    // 📱 MOBILE → PREVIEW
                    const link = document.createElement('a')
                    link.href = url
                    // link.target = '_blank'
                    link.click()
                } else {
                    // 💻 DESKTOP → NEW TAB
                    this.print(url)
                }
            },

            startTravel() {
                this.$swal.queue([{
                    title: "Démarrer l'expédition",
                    text: "Une fois que l'expédition aura démarré, aucun colis ne pourra être ajouté.",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#13cdc1',
                    cancelButtonColor: '#bd081c',
                    confirmButtonText: "Démarrer",
                    showLoaderOnConfirm: true,
                    preConfirm: () => {
                        return this.statusForm.post(route('travels.started', this.$page.props.travel), {
                            preserveScroll: true,
                            preserveState: true,
                            onSuccess: () => {
                                this.$swal.fire({
                                    icon: 'success',
                                    title: "Expédition",
                                    text: "Expédition démarrée avec succès.",
                                })
                            },
                            onError: (error) => {
                                if (error.status) {
                                    this.$swal.fire({
                                        icon: 'error',
                                        title: error.status,
                                    })
                                } else {
                                    this.$swal.fire({
                                        icon: 'error',
                                        title: "Oups, impossible de démarrer l'expédition. Quelque chose s'est mal passé.",
                                    })
                                }
                            }
                        })
                    }
                }])
            },

            finishTravel() {
                this.$swal.queue([{
                    title: "Terminer l'expédition",
                    text: "Êtes vous sûr de vouloir marquer l'expédition comme terminée ?",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#f97316',
                    cancelButtonColor: '#bd081c',
                    confirmButtonText: "Terminé",
                    showLoaderOnConfirm: true,
                    preConfirm: () => {
                        return this.statusFinishedForm.post(route('travels.finished', this.$page.props.travel), {
                            preserveScroll: true,
                            preserveState: true,
                            onSuccess: () => {
                                this.$swal.fire({
                                    icon: 'success',
                                    title: "Expédition",
                                    text: "Expédition terminée.",
                                })
                            },
                            onError: (error) => {
                                this.$swal.fire({
                                    icon: 'error',
                                    title: "Oups, impossible de terminer l'expédition. Quelque chose s'est mal passé.",
                                })
                            }
                        })
                    }
                }])
            },

            embeddedAllPackage() {
                this.$swal.queue([{
                    title: "Embarquer tous les colis",
                    text: "Voulez vous embarquer tous les colis ?",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#eab308',
                    cancelButtonColor: '#bd081c',
                    confirmButtonText: "Embarquer",
                    showLoaderOnConfirm: true,
                    preConfirm: () => {
                        return this.statusPackForm.post(route('travels.embedded-all-package', this.$page.props.travel), {
                            preserveScroll: true,
                            preserveState: true,
                            onSuccess: () => {
                                this.$swal.insertQueueStep({
                                    icon: 'success',
                                    title: "Colis",
                                    text: "Embarqués avec succès.",
                                })
                            },
                            onError: (error) => {
                                this.$swal.insertQueueStep({
                                    icon: 'error',
                                    title: "Oups, impossible d'embarquer tous les colis. Quelque chose s'est mal passé.",
                                })
                            }
                        })
                    }
                }])
            },

            makeTravelHasAvailable() {
                this.$swal.queue([{
                    title: "Rendre l'expédition disponible",
                    text: "Voulez vous marquer l'expédition comme disponible en magasin ? Cette action rendra tout les colis disponibles en magasin.",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#966641',
                    cancelButtonColor: '#bd081c',
                    confirmButtonText: "Rendre disponible",
                    showLoaderOnConfirm: true,
                    preConfirm: () => {
                        return this.statusForm.post(route('travels.available', this.$page.props.travel), {
                            preserveScroll: true,
                            preserveState: true,
                            onSuccess: () => {
                                this.$swal.insertQueueStep({
                                    icon: 'success',
                                    title: "Expédition",
                                    text: "Disponible en magasin.",
                                })
                            },
                            onError: (error) => {
                                this.$swal.insertQueueStep({
                                    icon: 'error',
                                    title: "Oups, impossible de rendre l'expédition disponible en magasin. Quelque chose s'est mal passé.",
                                })
                            }
                        })
                    }
                }])
            },

            makeTravelHasClosed() {
                this.$swal.queue([{
                    title: "Fermer l'expédition",
                    text: "Êtes vous sûr de vouloir fermer l'expédition ?",
                    icon: 'error',
                    showCancelButton: true,
                    confirmButtonColor: '#64748b',
                    cancelButtonColor: '#bd081c',
                    confirmButtonText: "Fermer",
                    showLoaderOnConfirm: true,
                    preConfirm: () => {
                        return this.statusForm.post(route('travels.closed', this.$page.props.travel), {
                            preserveScroll: true,
                            preserveState: true,

                            onSuccess: () => {
                                this.$swal.fire({
                                    icon: 'success',
                                    title: "Expédition",
                                    text: "Expédition fermée.",
                                })
                            },

                            onError: (error) => {
                                console.log(error)
                                this.$swal.fire({
                                    icon: 'error',
                                    title: error.status
                                })
                            }
                        })
                    }
                }])
            },

            sendAlert() {
                this.$swal.fire({
                    title: "Envoyer une alerte",
                    icon: "warning",
                    input: "textarea",
                    inputPlaceholder: "Tapez votre message...",
                    inputAttributes: {
                        rows: 4,
                        maxlength: 160
                    },

                    showCancelButton: true,
                    confirmButtonText: "Prévisualiser",
                    confirmButtonColor: "#eab308",

                    preConfirm: (message) => {
                        if (!message) {
                            this.$swal.showValidationMessage("Le message est obligatoire")
                            return false
                        }
                        return message
                    }
                }).then((result) => {

                    if (!result.isConfirmed) return

                    const message = result.value
                    const time = new Date().toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                    })

                    this.$swal.fire({
                        title: "Aperçu du message",
                        background: "#e2e8f0",
                        html: `
                            <div style="background:#e2e8f0;padding:20px;border-radius:10px;min-height:150px">
                                <div style="max-width:80%;background:#e0f2fe;padding:7px 12px;border-radius:15px;
                                    border-top-right-radius:0;box-shadow:0 1px 1px rgba(0,0,0,0.2);font-size:14px;
                                    color:#111;text-align:left;white-space:pre-line;position:relative;
                                ">
                                    ${message}
                                    
                                    <div style="text-align:right;font-size:11px;color:#667781">
                                        ${time} ✓✓
                                    </div>
                                </div>
                            </div>
                        `,

                        showCancelButton: true,
                        confirmButtonText: "Envoyer",
                        confirmButtonColor: "#16a34a",
                        cancelButtonText: "Modifier",

                        preConfirm: () => {
                            return this.delayForm
                                .transform((data) => ({
                                    ...data,
                                    message: message,
                                }))
                                .post(route('travels.delay', this.$page.props.travel), {
                                    preserveScroll: true,
                                    preserveState: true,

                                    onSuccess: () => {
                                        this.$swal.fire({
                                            icon: "success",
                                            title: "Envoyé",
                                            text: "Message envoyé avec succès"
                                        })
                                    }
                                })
                        }
                    }).then((confirmResult) => {
                        if (confirmResult.dismiss === this.$swal.DismissReason.cancel) {
                            this.sendAlert()
                        }
                    })
                })
            }
        }
    })
</script>
<style scoped>
    .shipping-clauses :deep(h1),
    .shipping-clauses :deep(h2),
    .shipping-clauses :deep(h3) {
        font-weight: 700;
        margin-top: 0.5rem;
        margin-bottom: 0.5rem;
    }

    .shipping-clauses :deep(p) {
        margin-bottom: 0.5rem;
    }

    .shipping-clauses :deep(strong) {
        font-weight: 700;
    }

    .shipping-clauses :deep(ul) {
        list-style-type: disc;
        padding-left: 1.25rem;
        margin-bottom: 0.60rem;
    }

    .shipping-clauses :deep(li) {
        margin-bottom: 0.15rem;
    }
</style>